/**
 * Centralized date utilities for handling Firestore timestamps and various date formats
 */

import {Timestamp} from "firebase/firestore";

/**
 * Safely converts various Firestore date formats to a JavaScript Date object
 * Handles: Timestamp objects, Date objects, objects with toDate(), objects with seconds, strings
 */
export function convertToDate(date: any): Date {
  if (!date) return new Date();

  let convertedDate: Date;

  if (date instanceof Date) {
    convertedDate = date;
  } else if (date instanceof Timestamp) {
    convertedDate = date.toDate();
  } else if (date.toDate && typeof date.toDate === "function") {
    convertedDate = date.toDate();
  } else if (date.seconds && typeof date.seconds === "number") {
    convertedDate = new Date(date.seconds * 1000);
  } else if (typeof date === "string") {
    convertedDate = new Date(date);
  } else if (typeof date === "number") {
    convertedDate = new Date(date);
  } else {
    // Unknown format - return current date as fallback
    console.warn("Unknown date format, using current date as fallback:", date);
    return new Date();
  }

  // Validate the converted date - if invalid, return current date
  if (isNaN(convertedDate.getTime())) {
    console.warn(
      "Invalid date detected, using current date as fallback:",
      date
    );
    return new Date();
  }

  return convertedDate;
}

/**
 * Safely formats a date for display (returns empty string if invalid)
 * @param date - Any date format supported by convertToDate
 * @param options - Intl.DateTimeFormatOptions for customizing the format
 */
export function getDisplayDate(
  date: any,
  options?: Intl.DateTimeFormatOptions
): string {
  if (!date) return "";

  try {
    const convertedDate = convertToDate(date);
    if (isNaN(convertedDate.getTime())) return "";

    // Use consistent formatting options to avoid locale-related inconsistencies
    const defaultOptions: Intl.DateTimeFormatOptions = {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    };

    return convertedDate.toLocaleDateString("en-US", {
      ...defaultOptions,
      ...options,
    });
  } catch (error) {
    console.warn("Error formatting date:", error);
    return "";
  }
}

/**
 * Safely formats a time for display (returns empty string if invalid)
 * @param date - Any date format supported by convertToDate
 * @param options - Options for time formatting
 */
export function getDisplayTime(
  date: any,
  options: Intl.DateTimeFormatOptions = {hour: "2-digit", minute: "2-digit"}
): string {
  if (!date) return "";

  try {
    const convertedDate = convertToDate(date);
    if (isNaN(convertedDate.getTime())) return "";

    // Use consistent formatting options to avoid locale-related inconsistencies
    const defaultOptions: Intl.DateTimeFormatOptions = {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    };

    return convertedDate.toLocaleTimeString("en-US", {
      ...defaultOptions,
      ...options,
    });
  } catch (error) {
    console.warn("Error formatting time:", error);
    return "";
  }
}

/**
 * Formats a date and time together for display
 * @param date - Any date format supported by convertToDate
 * @param separator - String to separate date and time (default: " at ")
 */
export function getDisplayDateTime(
  date: any,
  separator: string = " at "
): string {
  const displayDate = getDisplayDate(date);
  const displayTime = getDisplayTime(date);

  if (!displayDate && !displayTime) return "";
  if (!displayTime) return displayDate;
  if (!displayDate) return displayTime;

  return `${displayDate}${separator}${displayTime}`;
}

/**
 * Formats a date with full details (date, day, time)
 * @param date - Any date format supported by convertToDate
 */
export function getFullDisplayDateTime(date: any): string {
  if (!date) return "";

  try {
    const convertedDate = convertToDate(date);
    if (isNaN(convertedDate.getTime())) return "";

    return convertedDate.toLocaleString(undefined, {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (error) {
    console.warn("Error formatting full date time:", error);
    return "";
  }
}

/**
 * Converts a Date or string to a Firestore Timestamp
 */
export function convertToTimestamp(date: Date | string): Timestamp {
  if (typeof date === "string") {
    return Timestamp.fromDate(new Date(date));
  }
  return Timestamp.fromDate(date);
}

/**
 * Converts a Firestore Timestamp to a Date
 */
export function convertFromTimestamp(timestamp: Timestamp): Date {
  return timestamp.toDate();
}

/**
 * The time offset after session start when it's considered "done" (2 hours in milliseconds)
 */
export const SESSION_DONE_OFFSET = 2 * 60 * 60 * 1000;

/**
 * Checks if a session is considered "done" based on its start date and the session done offset.
 * @param sessionDate - Any date format supported by convertToDate
 * @returns boolean - True if the session is completed or in the past (date + offset <= now)
 */
export function isSessionDone(sessionDate: any): boolean {
  if (!sessionDate) return false;
  const date = convertToDate(sessionDate);
  const now = new Date();
  return (date.getTime() + SESSION_DONE_OFFSET) <= now.getTime();
}

/**
 * Formats a timestamp for logging/debugging purposes
 */
export function formatLoggedAtDate(timestamp: any): string {
  try {
    const convertedDate = convertToDate(timestamp);
    if (isNaN(convertedDate.getTime())) return "Invalid date";

    return convertedDate.toLocaleString();
  } catch (error) {
    return "Invalid date";
  }
}

/**
 * Formats a join date for user management in DD MMM 'YY format
 */
export function formatJoinDate(timestamp: any): string {
  try {
    const convertedDate = convertToDate(timestamp);
    if (isNaN(convertedDate.getTime())) return "N/A";

    const day = convertedDate.getDate();
    const month = convertedDate.toLocaleString("en-US", {month: "short"});
    const year = String(convertedDate.getFullYear()).slice(-2);
    return `${day} ${month} '${year}`;
  } catch (error) {
    return "N/A";
  }
}

/**
 * Formats a Date into YYYY-MM-DD for a specific IANA timezone using Intl API
 */
export function formatDateYYYYMMDDInTimeZone(
  date: any,
  timeZone: string
): string {
  const d = convertToDate(date);
  if (isNaN(d.getTime())) return "";

  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  // en-CA yields YYYY-MM-DD format already
  return fmt.format(d);
}

/**
 * Formats a Date into HH:MM (24h) for a specific IANA timezone using Intl API
 */
export function formatTimeHHMMInTimeZone(
  date: any,
  timeZone: string
): string {
  const d = convertToDate(date);
  if (isNaN(d.getTime())) return "";

  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
  })
    .formatToParts(d)
    .reduce<Record<string, string>>((acc, p) => {
      if (p.type !== "literal") acc[p.type] = p.value;
      return acc;
    }, {});

  return `${parts.hour ?? "00"}:${parts.minute ?? "00"}`;
}

// Convenience helpers for Europe/Amsterdam
export function getAmsterdamDateISO(date: any): string {
  return formatDateYYYYMMDDInTimeZone(date, "Europe/Amsterdam");
}

export function getAmsterdamTimeHHMM(date: any): string {
  return formatTimeHHMMInTimeZone(date, "Europe/Amsterdam");
}